import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import { Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import isNil from 'lodash/isNil'
import {
  BillingProfileRecord,
  BillingProfileSortField,
} from '../../api/dto/client-billing-profile.dto'
import type { BillingProfileSort } from './useBillingProfileFilters'
import { BillingProfileRowActions } from './BillingProfileRowActions'
import {
  DISPLAY_DATE_FORMAT,
  formatDueDayLabel,
  type PaymentType,
  type SyncSource,
} from './types'
import { formatPaymentTypeLabel } from './bulkUpload.helpers'
import { NoneText } from '../shared/finance.styles'
import {
  CompanyCell,
  DueDayCell,
  SyncCell,
  SyncSourceTag,
} from './ClientBillingProfileManagement.styles'

interface UseBillingProfileColumnsParams {
  canWriteBillingProfile: boolean
  /** The server-side sort the column headers show. */
  sort: BillingProfileSort | null
  onEdit: (record: BillingProfileRecord) => void
  /** Restoring a company, which is confirmed here. */
  onToggleStatus: (record: BillingProfileRecord) => void
  /** Removing one, which has its own dialog naming what each recipient loses. */
  onRemove: (record: BillingProfileRecord) => void
}

const INVOICE_RECIPIENTS_HINT =
  'Addresses this company’s invoices are sent to, including CC'

/** Header sort state for a server-sorted column. */
const sortableColumn = (
  field: BillingProfileSortField,
  sort: BillingProfileSort | null
) => ({
  key: field,
  sorter: true,
  sortOrder:
    sort?.field === field
      ? sort.order === 'asc'
        ? ('ascend' as const)
        : ('descend' as const)
      : null,
})

export const useBillingProfileColumns = ({
  canWriteBillingProfile,
  sort,
  onEdit,
  onToggleStatus,
  onRemove,
}: UseBillingProfileColumnsParams): ColumnsType<BillingProfileRecord> =>
  useMemo(
    () => [
      {
        title: 'Company name',
        dataIndex: 'clientName',
        ...sortableColumn(BillingProfileSortField.ClientName, sort),
        // Frozen so the company stays on screen while the payment and sync
        // columns scroll past it. A frozen column carries its own width.
        fixed: 'left',
        width: 320,
        ellipsis: true,
        render: (name: string) => <CompanyCell>{name}</CompanyCell>,
      },
      {
        title: (
          <Tooltip title={INVOICE_RECIPIENTS_HINT}>
            <span>Invoice recipients</span>
          </Tooltip>
        ),
        key: 'toEmails',
        width: 140,
        // To plus CC, matching the addresses the User tab lists for a company.
        render: (_: unknown, record: BillingProfileRecord) =>
          (record.toEmails ?? []).length + (record.ccEmails ?? []).length,
      },
      {
        title: 'Payment type',
        dataIndex: 'paymentType',
        ...sortableColumn(BillingProfileSortField.PaymentType, sort),
        width: 130,
        render: (type: PaymentType | null) =>
          isNil(type) ? (
            <NoneText>—</NoneText>
          ) : (
            <span>{formatPaymentTypeLabel(type)}</span>
          ),
      },
      {
        title: 'Plan effective date',
        dataIndex: 'planEffectiveDate',
        ...sortableColumn(BillingProfileSortField.PlanEffectiveDate, sort),
        width: 150,
        render: (date: string | null) =>
          isNil(date) ? (
            <NoneText>—</NoneText>
          ) : (
            <span>{dayjs(date).format(DISPLAY_DATE_FORMAT)}</span>
          ),
      },
      {
        title: 'Due day',
        dataIndex: 'dueDay',
        ...sortableColumn(BillingProfileSortField.DueDay, sort),
        width: 100,
        render: (day: number | null) =>
          isNil(day) ? (
            <NoneText>—</NoneText>
          ) : (
            <DueDayCell>{formatDueDayLabel(day)}</DueDayCell>
          ),
      },
      {
        title: 'Last sync',
        dataIndex: 'lastSyncedAt',
        ...sortableColumn(BillingProfileSortField.LastSyncedAt, sort),
        width: 160,
        render: (syncedAt: string | null, record: BillingProfileRecord) => {
          if (isNil(syncedAt)) return <NoneText>—</NoneText>
          const source: SyncSource | null = record.lastSyncSource
          return (
            <SyncCell>
              <span>{dayjs(syncedAt).format(DISPLAY_DATE_FORMAT)}</span>
              {isNil(source) ? null : (
                <SyncSourceTag $auto={source === 'AUTO'}>
                  {source === 'AUTO' ? 'Auto' : 'Manual'}
                </SyncSourceTag>
              )}
            </SyncCell>
          )
        },
      },
      {
        title: 'Action',
        key: 'actions',
        width: 90,
        render: (_: unknown, record: BillingProfileRecord) =>
          canWriteBillingProfile ? (
            <BillingProfileRowActions
              record={record}
              onEdit={onEdit}
              onToggleStatus={onToggleStatus}
              onRemove={onRemove}
            />
          ) : (
            <NoneText>—</NoneText>
          ),
      },
    ],
    [canWriteBillingProfile, sort, onEdit, onToggleStatus, onRemove]
  )
